import { z } from "zod";
import type { FormRule } from "@/features/form-editor/types/form-rule-types";
import { FormRulesEnvelopeSchema } from "@/features/form-editor/types/form-rule-types";
import type { LayoutElement } from "@/lib/layoutRenderer/schemas/layout.schema";
import type { LayoutJsonEnvelope } from "./layout-json-envelope.schema";

/**
 * OpenAPI `ComparisonType` — used by {@link DataQuerySchema}.
 */
export const ComparisonTypeSchema = z.number().int().min(0).max(7);

/**
 * OpenAPI `DataQuery` — entity custom-field filters (`EntityDataQueries` on job/ticket lists).
 */
export const DataQuerySchema = z.object({
	columnDefinitionId: z.string().uuid().nullable().optional(),
	name: z.string().nullable().optional(),
	comparisonType: ComparisonTypeSchema,
	textValue: z.string().nullable().optional(),
	numericValue: z.number().nullable().optional(),
	dateValue: z.string().nullable().optional(),
});

/**
 * OpenAPI `ColumnValue` — job/ticket `data` array items.
 */
export const ColumnValueSchema = z.object({
	id: z.string().nullable().optional(),
	name: z.string().nullable().optional(),
	value: z.unknown().nullable().optional(),
});

/**
 * OpenAPI `EntityDataValidation` — nested validation rules for a field definition.
 */
export const EntityDataValidationSchema = z.object({
	rangeMax: z.number().nullable().optional(),
	rangeMaxExclusive: z.number().nullable().optional(),
	rangeMin: z.number().nullable().optional(),
	rangeMinExclusive: z.number().nullable().optional(),
	maxLength: z.number().int().nullable().optional(),
	regex: z.string().nullable().optional(),
	validValues: z.array(z.string()).nullable().optional(),
});

export type EntityDataValidation = z.infer<typeof EntityDataValidationSchema>;

/**
 * Every {@link EntityDataValidation} key set to `null` — required on every field type sent to the API.
 */
export const EMPTY_ENTITY_DATA_VALIDATION: EntityDataValidation = {
	rangeMax: null,
	rangeMaxExclusive: null,
	rangeMin: null,
	rangeMinExclusive: null,
	maxLength: null,
	regex: null,
	validValues: null,
};

/**
 * OpenAPI `FieldDefinition`.
 */
export const FieldDefinitionSchema = z.object({
	id: z.string().uuid(),
	name: z.string(),
	label: z.string().nullable(),
	dataType: z.string(),
	required: z.boolean().default(false),
	validationRules: EntityDataValidationSchema.nullable().optional(),
});

/**
 * OpenAPI `FormDefinition`.
 */
export const FormDefinitionSchema = z.object({
	id: z.string().uuid(),
	version: z.number().int(),
	active: z.boolean(),
	layoutJson: z.string().nullable().optional(),
	fieldDefinitions: z.array(FieldDefinitionSchema).nullable().optional(),
	formRules: FormRulesEnvelopeSchema.nullable().optional(),
});

/**
 * OpenAPI `CreateFormDefinitionInfo` — POST /form-definitions
 */
export const CreateFormDefinitionInfoSchema = z.object({
	layoutJson: z.string().nullable().optional(),
	fieldDefinitions: z.array(FieldDefinitionSchema).nullable().optional(),
	formRules: FormRulesEnvelopeSchema.nullable().optional(),
	subOrganizationId: z.string().nullable().optional(),
	jobTypeId: z.string().nullable().optional(),
	ticketTypeId: z.string().nullable().optional(),
	assetTypeId: z.string().nullable().optional(),
	recordTypeId: z.string().nullable().optional(),
});

/**
 * OpenAPI `EditFormDefinitionInfo` — PUT /form-definitions/{id}
 * Scope ids (`subOrganizationId`, `jobTypeId`, etc.) belong on {@link CreateFormDefinitionInfoSchema} only, not on edit.
 */
export const EditFormDefinitionInfoSchema = z.object({
	layoutJson: z.string().nullable().optional(),
	fieldDefinitions: z.array(FieldDefinitionSchema).nullable().optional(),
	formRules: FormRulesEnvelopeSchema.nullable().optional(),
});

/**
 * OpenAPI `EditFormLayoutInfo` — PUT /form-definitions/{id}/layout
 */
export const EditFormLayoutInfoSchema = z.object({
	layoutJson: z.string().nullable().optional(),
});

/**
 * OpenAPI `ScopeFields` — POST /form-definitions/{id}/version
 */
export const ScopeFieldsSchema = z.object({
	subOrganizationId: z.string().nullable().optional(),
	jobTypeId: z.string().nullable().optional(),
	ticketTypeId: z.string().nullable().optional(),
	assetTypeId: z.string().nullable().optional(),
	recordTypeId: z.string().nullable().optional(),
});

/**
 * Query params for GET /form-definitions/count and GET /form-definitions/{start}/{count}
 */
export const FormDefinitionsListParamsSchema = z.object({
	JobTypeId: z.string().uuid().optional(),
	TicketTypeId: z.string().uuid().optional(),
	AssetTypeId: z.string().uuid().optional(),
	CreatedBefore: z.string().optional(),
	CreatedAfter: z.string().optional(),
	UpdatedBefore: z.string().optional(),
	UpdatedAfter: z.string().optional(),
	OrganizationId: z.string().optional(),
	SubOrganizationId: z.string().optional(),
	ForOrganizationUser: z.string().uuid().optional(),
	"OrderBy.Fields": z.array(z.string()).optional(),
	"OrderBy.Descending": z.boolean().optional(),
});

/**
 * Query params for GET /form-definitions/current and GET /form-definitions/active
 */
export const FormDefinitionScopeQuerySchema = z.object({
	SubOrganizationId: z.string().optional(),
	JobTypeId: z.string().optional(),
	TicketTypeId: z.string().optional(),
	AssetTypeId: z.string().optional(),
	RecordTypeId: z.string().optional(),
});

export type ComparisonType = z.infer<typeof ComparisonTypeSchema>;
export type DataQuery = z.infer<typeof DataQuerySchema>;
export type ColumnValue = z.infer<typeof ColumnValueSchema>;
export type FieldDefinition = z.infer<typeof FieldDefinitionSchema>;
export type FormDefinition = z.infer<typeof FormDefinitionSchema>;

/**
 * Result of TanStack Query `select` on form-definition endpoints.
 * Wire `layoutJson` is parsed to {@link LayoutJsonEnvelope}; rules are resolved to a flat array.
 */
export type FormDefinitionQueryData = Omit<
	FormDefinition,
	"layoutJson" | "formRules"
> & {
	/** Parsed layout tree from envelope.layout (or layout-only root). */
	layout: LayoutElement | null;
	/** Typed metadata wrapper — never contains formRules after normalization. */
	layoutEnvelope: LayoutJsonEnvelope | null;
	/** Resolved form rules — never null. */
	formRules: FormRule[];
	/** Original API wire string — used for legacy formRules fallback after select. */
	wireLayoutJson: string | null;
	/**
	 * @deprecated Use {@link layout} — alias kept for consumers expecting parsed `layoutJson`.
	 */
	layoutJson: LayoutElement | null;
};
export type CreateFormDefinitionInfo = z.infer<
	typeof CreateFormDefinitionInfoSchema
>;
export type EditFormDefinitionInfo = z.infer<
	typeof EditFormDefinitionInfoSchema
>;
export type EditFormLayoutInfo = z.infer<typeof EditFormLayoutInfoSchema>;
export type ScopeFields = z.infer<typeof ScopeFieldsSchema>;
export type FormDefinitionsListParams = z.infer<
	typeof FormDefinitionsListParamsSchema
>;
export type FormDefinitionScopeQuery = z.infer<
	typeof FormDefinitionScopeQuerySchema
>;
